import { X, Package, MapPin } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { t } from "@/i18n";

export const ProductModal = ({ product, isOpen, onClose, locationName }) => {
  if (!product) return null;

  const isOutOfStock = product.stock === 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[550px] p-0 overflow-hidden rounded-2xl">
        {/* Product Image */}
        <div className="relative aspect-square sm:aspect-[4/3] bg-[#f2f4f3]">
          <img
            src={product.image_url}
            alt={product.name}
            className="w-full h-full object-cover"
            data-testid="product-modal-image"
            onError={(e) => {
              e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200' viewBox='0 0 200 200'%3E%3Crect fill='%23f2f4f3' width='200' height='200'/%3E%3Ctext fill='%239ca3af' font-family='sans-serif' font-size='14' x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle'%3E" + t('common.noImage') + "%3C/text%3E%3C/svg%3E";
            }}
          />
          {isOutOfStock && (
            <span className="absolute top-4 right-4 bg-gray-800 text-white px-3 py-1.5 rounded-full text-xs font-semibold">
              {t('products.outOfStock')}
            </span>
          )}
          {/* Close button */}
          <button
            onClick={() => onClose(false)}
            className="absolute top-4 left-4 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-colors"
            data-testid="product-modal-close"
            aria-label={t('common.close')}
          >
            <X className="w-5 h-5 text-[#111827]" />
          </button>
        </div>

        {/* Product Details */}
        <div className="p-6">
          <DialogHeader className="mb-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <DialogTitle className="text-2xl font-bold text-[#111827] mb-1">
                  {product.name}
                </DialogTitle>
                {product.category && (
                  <span className="text-sm text-[#9ca3af]">{product.category}</span>
                )}
                {locationName && (
                  <div className="flex items-center gap-1.5 text-sm text-[#6b7280] mt-1">
                    <MapPin className="w-3.5 h-3.5" />
                    <span>{locationName}</span>
                  </div>
                )}
              </div>
              <div className="text-right flex-shrink-0">
                <p className="mono text-2xl font-bold text-[#1a4d2e]">
                  {product.price.toFixed(2)} {t('products.currency')}
                </p>
                {product.sgr > 0 && (
                  <p className="text-sm text-[#ff4d00] font-medium">
                    +{(product.sgr * 100).toFixed(0)} {t('products.sgrDeposit')}
                  </p>
                )}
              </div>
            </div>
          </DialogHeader>

          {/* Description */}
          {product.description && (
            <div className="mb-6">
              <p className="text-[#6b7280] leading-relaxed">
                {product.description}
              </p>
            </div>
          )}

          {/* Stock Info */}
          <div className="flex items-center gap-2 mb-4 p-3 bg-[#f2f4f3] rounded-xl">
            <Package className="w-5 h-5 text-[#1a4d2e]" />
            <span className="text-sm text-[#111827]">
              <span className="font-semibold">{product.stock}</span> {t('products.inStock')}
            </span>
          </div>

          {/* SGR Info */}
          {product.sgr > 0 && (
            <div className="p-3 bg-[#fff7ed] border border-[#ffedd5] rounded-xl">
              <p className="text-sm text-[#9a3412]">
                {t('products.sgrInfo', { amount: (product.sgr * 100).toFixed(0) })}
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
